import { useRequestEditEmployeePermissions } from '@/hooks/httpRequest';
import { confirm } from '@/components/custom/react-confirm/Confirm';
import { Ellipsis, Eye, ShieldCheck, X } from 'lucide-react';
import { confirmAction, Loader } from '@/components/custom';
import { notify, notifyInternalMessage } from '@/functions';
import { SecuredComponent } from '@/components/hoc';
import { useNavigate } from 'react-router-dom';
import { IEmployee } from '@/interfaces';
import { useEffect } from 'react';
import routes from '@/lib/routes';
import {
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenu,
  TableCell,
  Button
} from '@/components/ui';

export const DataTableBodyCellOptions = ({ _id, username, roles }: IEmployee) => {

  const { triggerEditPermissions, isLoadingEditPermissions, error: errorEditPermissions } = useRequestEditEmployeePermissions();
  const navigate = useNavigate();

  useEffect(
    () => {
      errorEditPermissions &&
        notify((errorEditPermissions as any)?.internalMessage, 'error');
    },
    [errorEditPermissions]
  );

  const handleOnClickMenuRemoveUser = async (e: any) => {
    // Stop propagation of event
    e.stopPropagation();

    const yes = await confirm(
      <p className='my-2'>
        Suppression des rôles et permissions de <span className='text-purple-600'>{username}</span>.
        <br />
        Voulez-vous confirmer l'action ?
      </p>
    );

    if (!yes) {
      return;
    }

    // Perform action
    triggerEditPermissions({
      employee: false,
      permissions: [],
      root: false,
      userId: _id
    })
      .then(() => notify(`L'utilisateur a été supprimé avec succès.`));
  };

  const handleOnClickBtnDetails = (e: any) => {
    e.stopPropagation();
    navigate(routes.nav.users.single.getHref(_id));
  };

  const handleOnClickDefault = (e: any) =>
    e.stopPropagation();

  const handleOnClickBtnSetRoot = async (e: any, assign: boolean) => {
    e.stopPropagation();

    const yes = await confirmAction(
      <div className='my-2 flex flex-col'>
        <p className='space-x-1 flex items-center'>
          <span>
            Vous êtes sur le point {assign ? `d'assigner` : 'de retirer'} le rôle de
          </span>
          <span className='text-red-500'>
            ROOT
          </span>
          <span>
            à
          </span>
          <span className='text-purple-600'>
            {username}
          </span>.
        </p>
        <p>
          Voulez-vous confirmer l'action ?
        </p>
      </div>
    );

    if (!yes) {
      return;
    }

    // Perform action
    triggerEditPermissions({
      permissions: roles.permissions,
      employee: true,
      root: assign,
      userId: _id
    })
      .catch(notifyInternalMessage);

  };

  let content = (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger asChild>
        <Button
          onClick={handleOnClickDefault}
          className='h-7 py-1'
          variant={'ghost'}
          size={'sm'}>
          <Ellipsis size={16} />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        className='min-w-36 max-w-52'
        align='end'>
        <DropdownMenuGroup>
          <DropdownMenuItem
            className='cursor-pointer'
            onClick={handleOnClickBtnDetails}>
            <Eye size={16} />
            Détails
          </DropdownMenuItem>
          <SecuredComponent>
            <DropdownMenuItem
              className='cursor-pointer'
              onClick={e => handleOnClickBtnSetRoot(e, !roles?.root)}>
              <ShieldCheck size={16} />
              {
                !roles?.root
                  ? <span>
                    Assigner le rôle <span className='text-red-500'>ROOT</span>.
                  </span>
                  : <span>
                    Retirer le rôle <span className='text-red-500'>ROOT</span>.
                  </span>
              }
            </DropdownMenuItem>
          </SecuredComponent>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            className='cursor-pointer text-red-500'
            onClick={handleOnClickMenuRemoveUser}>
            <X size={16} />
            Supprimer
          </DropdownMenuItem>
        </DropdownMenuGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );

  if (isLoadingEditPermissions) {
    content = <Loader className='h-5 m-1' />;
  }

  return (
    <TableCell className='py-1 px-2 w-0 text-right'>
      {content}
    </TableCell>
  );

};

export default DataTableBodyCellOptions;
